import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, FileText, Search, Image, Download } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper to get full image URL - handles old preview URLs and relative paths
const getFullImageUrl = (url) => {
  if (!url) return null;
  // Already a relative path - prepend API URL
  if (url.startsWith('/api/uploads')) return `${API}${url}`;
  if (url.startsWith('/uploads')) return `${API}/api${url}`;
  // Old preview URL - extract path and use current API
  if (url.includes('.preview.emergentagent.com')) {
    const match = url.match(/\/api\/uploads\/.+$/);
    if (match) return `${API}${match[0]}`;
    const uploadMatch = url.match(/\/uploads\/.+$/);
    if (uploadMatch) return `${API}/api${uploadMatch[0]}`;
  }
  // External URL (http/https) - return as-is
  if (url.startsWith('http')) return url;
  // Relative path without leading slash
  return `${API}/${url}`;
};

export default function ReferenceSheets() {
  const navigate = useNavigate();
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [topicFilter, setTopicFilter] = useState('all');
  const [topics, setTopics] = useState([]);

  useEffect(() => {
    fetchTopics();
  }, []);

  useEffect(() => {
    fetchSheets();
  }, [search, topicFilter]);

  const fetchTopics = async () => {
    try {
      const res = await fetch(`${API}/api/topics`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        // Transform to match expected format {key, display}
        const formattedTopics = data.map(t => ({
          key: t.topic_key,
          display: t.topic_display_name || t.topic_key
        }));
        setTopics(formattedTopics);
      }
    } catch (err) {
      console.error('Failed to fetch topics:', err);
    }
  };

  const fetchSheets = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (topicFilter !== 'all') params.append('topic', topicFilter);
      
      const res = await fetch(`${API}/api/reference-sheets?${params}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch reference sheets');
      const data = await res.json();
      setSheets(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-slate-600 hover:text-slate-900 mb-4"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <FileText className="w-8 h-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Reference Sheets</h1>
              <p className="text-slate-500">Quick reference guides and clinical resources</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="max-w-6xl mx-auto px-4 py-4">
        <div className="flex gap-4 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search reference sheets..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-white border-slate-200"
              data-testid="search-input"
            />
          </div>
          <Select value={topicFilter} onValueChange={setTopicFilter}>
            <SelectTrigger className="w-48 bg-white border-slate-200">
              <SelectValue placeholder="All Topics" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Topics</SelectItem>
              {topics.map((topic) => (
                <SelectItem key={topic.key} value={topic.key}>
                  {topic.display}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-4">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-red-700">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-center py-12 text-slate-500">Loading...</div>
        ) : sheets.length === 0 ? (
          <Card className="bg-white border-slate-200">
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">No Reference Sheets Available</h3>
              <p className="text-slate-500">
                {search || topicFilter !== 'all' 
                  ? 'Try adjusting your filters.'
                  : 'Check back later for new reference materials.'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {sheets.map((sheet) => (
              <Card
                key={sheet.sheet_id}
                className="bg-white border-slate-200 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer"
                onClick={() => navigate(`/reference-sheets/${sheet.sheet_id}`)}
                data-testid={`sheet-card-${sheet.sheet_id}`}
              >
                <CardContent className="p-4">
                  {/* Thumbnail preview */}
                  {sheet.png_urls?.length > 0 ? (
                    <div className="aspect-[4/3] bg-slate-100 rounded-lg mb-3 overflow-hidden">
                      <img
                        src={getFullImageUrl(sheet.png_urls[0])}
                        alt={sheet.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = '<div class="flex items-center justify-center h-full"><svg class="w-12 h-12 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg></div>';
                        }}
                      />
                    </div>
                  ) : (
                    <div className="aspect-[4/3] bg-slate-100 rounded-lg mb-3 flex items-center justify-center">
                      <Image className="w-12 h-12 text-slate-300" />
                    </div>
                  )}
                  
                  <h3 className="font-semibold text-slate-900 mb-1">{sheet.title}</h3>
                  
                  {sheet.description && (
                    <p className="text-sm text-slate-500 line-clamp-2 mb-2">{sheet.description}</p>
                  )}
                  
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex flex-wrap gap-1.5">
                      <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-200 text-xs">
                        {sheet.topic_key}
                      </Badge>
                      {sheet.subtopic_key && (
                        <Badge variant="outline" className="bg-blue-50 text-blue-600 border-blue-200 text-xs">
                          {sheet.subtopic_key}
                        </Badge>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-2 text-slate-400">
                      <Image className="w-4 h-4" />
                      <span className="text-xs">{sheet.png_urls?.length || 0}</span>
                      {sheet.pdf_url && (
                        <Download className="w-4 h-4 text-green-500" />
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
